import { ActionIcon, Box, Button, Grid, Group, Loader, Modal, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { notifications, showNotification } from '@mantine/notifications';
import React, { useEffect, useMemo } from 'react'
import { useApp } from '../../contexts/app.context';
import { DELETE_COMPANY, EXPORT_COMPANIES, GET_ALL_COMPANIES, GET_BILLING_DETAILS, RECHARGE_COMPANY, UPDATE_COMPANY } from '../../services/companies';
import Table from '../table';
import { FaCheck, FaSign, FaSignInAlt } from 'react-icons/fa';
import { Buffer } from 'buffer';
import { extenseRecurrenceType, formatMilliseconds, formatMoney, getExtenseDatetime } from '../../utility/util';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/auth.context';
import moment from 'moment-timezone';
import { useLanguage } from '../../contexts/language.context';
import { FaArrowLeft, FaPlus } from 'react-icons/fa6';
import { GET_ALL_PLANS } from '../../services/plans';
import InputField, { InputFieldMasks, InputFieldTypes } from '../input_field';

interface ListProps { }
interface ListHandle {
    refresh: () => void;
}

const List: React.ForwardRefRenderFunction<ListHandle, ListProps> = ({ }, ref) => {
    const [loading, setLoading] = React.useState(false);
    const [loadingExport, setLoadingExport] = React.useState(false);
    const [loadingDelete, setLoadingDelete] = React.useState(false);
    const [error, setError] = React.useState<any>(null);
    const [data, setData] = React.useState([]);
    const [updating, setUpdating] = React.useState<any>(null);
    const [addingCredits, setAddingCredits] = React.useState<any>(null);
    const [months, setMonths] = React.useState([]);
    const [planId, setPlanId] = React.useState<string>();
    const [plans, setPlans] = React.useState<any[]>([]);
    const [nextPayment, setNextPayment] = React.useState<string>();
    const [typeView, setTypeView] = React.useState<string>("general");

    const { str } = useLanguage();
    const { confirmDialog } = useApp();
    const navigate = useNavigate();
    const theme = useMantineTheme();

    const loadPlans = () => {
        GET_ALL_PLANS()
            .then((plans) => {
                setPlans(plans)
            })
            .catch(err => {
                showNotification({ message: err.message, color: "red" });
            })
    }

    const handleChangePlan = (planId: string) => {
        const old = planId;
        setPlanId(planId);
        UPDATE_COMPANY(updating.item.id, { active_plan: planId })
            .then(() => {
                loadBilling();
            })
            .catch((err: any) => {
                setPlanId(old);
                notifications.show({ message: err.message, color: "red" });
            })
    }

    const handleChangeNextPayment = (date: string) => {
        const old = date;
        setNextPayment(date);
        UPDATE_COMPANY(updating.item.id, { next_plan_payment: date })
            .then(() => {
                loadBilling();
            })
            .catch((err: any) => {
                setNextPayment(old);
                notifications.show({ message: err.message, color: "red" });
            })
    }

    const handleAddCredits = () => {
        RECHARGE_COMPANY(updating?.item?.id, addingCredits)
            .then(() => {
                GET_BILLING_DETAILS(updating?.item?.id)
                    .then(billing => {
                        setUpdating(u => ({ ...u, billing }));
                        setAddingCredits(null);
                    })
                    .catch(() => {
                        window.location.reload();
                    })
            })
            .catch(err => {
                showNotification({ message: err.message, color: "red" })
            })
    }

    const handleDeleteWorkspace = (companyId: string) => {

        setLoadingDelete(true);
        DELETE_COMPANY(companyId)
            .then(() => {
                setUpdating(null);
                loadCompanies();
            })
            .catch(err => {
                showNotification({ message: err.message, color: "red" });
            })
            .finally(() => {
                setLoadingDelete(false);
            })
    }

    const handleExport = () => {
        setLoadingExport(true);

        let months = [];
        let m = moment("2024-01-01T00:00:00");
        while (m < moment().endOf("month")) {
            months.push({ base: m.format() })
            m.add(1, "months");
        }

        let csvText = [
            [
                "status",
                "name",
                "email",
                "phone",
                "users",
                "total_dials",
                "used_credits",
                "total_credits",
                "plan",
                "next_payment",
                "domain",
                "signup_date",
                "total_payments",
                ...months.map(m => moment(m.base).format("MMMM/YYYY")),
            ],
            ...data.map(item => [
                item.billing?.activePaymentMethod
                    ? moment().diff(moment(item?.billing?.nextPayment), "days") > 0
                        ? "Not Payed"
                        : "Active"
                    : "Canceled"
                ,
                item.name,
                item.email,
                `+${item.phone_code ?? "1"}${item.phone}`.replace(/ |\(|\)|\-|\+|\_/g, ""),
                (item.users || []).length,
                `${formatMilliseconds(item.billing?.consumption * 1000)}`,
                `${formatMoney(item.billing?.creditsUsage)}`,
                `${formatMoney(item.billing?.totalCredits ?? 0)}`,
                item.billing?.plan?.title,
                item.billing?.nextPayment ? moment(item.billing?.nextPayment).format("YYYY-MM-DD") : null,
                (item.domains ?? [])[0] ? `https://${item.domains[0].url}` : "",
                item.createdAt,
                item.plan_payments.reduce((pv, p) => pv + ((p.paid_at && !p.refund_at) ? p.value : 0), 0),
                ...months.map(month => ((item.plan_payments ?? [])
                    .filter(p => (
                        p.paid_at && !p.refund_at &&
                        moment(p.paid_at).isBetween(
                            moment(month.base).startOf("month"),
                            moment(month.base).endOf("month")
                        )
                    ))
                    .reduce((pv, p) => pv + p.value, 0)
                ).toFixed(2))
            ])
        ].map(items => items.map(it => `"${(it ?? "").toString().trim()}"`).join(",")).join("\n")

        const byteCharacters = Buffer.from(csvText, "utf-8");
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) { byteNumbers[i] = byteCharacters[i]; }
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: "application/csv" });

        const url = URL.createObjectURL(blob);;
        const a = document.createElement('a');
        a.href = url;
        a.download = `Awaz_Companies.csv`;
        a.click();
        URL.revokeObjectURL(url);

        setLoadingExport(false);
    }

    const COLUMNS = typeView === "payments"
        ? [
            {
                title: "Company", key: 'name', render: (item) => <Box>
                    <Text size="sm">{item.name}</Text>
                </Box>
            },
            {
                title: "Total Payed", key: "total_payments", render: (item) => (
                    item.plan_payments.reduce((pv, p) => pv + ((p.paid_at && !p.refund_at) ? p.value : 0), 0).toFixed(2)
                )
            },
            ...months.map(month => ({
                title: moment(month.base).format("MMMM/YYYY"),
                key: `${month.base}`,
                render: (item) => ((item.plan_payments ?? [])
                    .filter(p => (
                        p.paid_at &&
                        moment(p.paid_at).isBetween(
                            moment(month.base).startOf("month"),
                            moment(month.base).endOf("month")
                        )
                    ))
                    .reduce((pv, p) => pv + p.value, 0)
                ).toFixed(2),
            }))
        ]
        : [
            { title: str("TITLE"), key: 'name', render: (item) => item.name },
            { title: str("EMAIL"), key: 'email', render: (item) => item.email },
            { title: str("PHONE"), key: 'phone', render: (item) => `+${item.phone_code ?? ""}${item.phone}` },
            // {
            //     title: str("COMPANIES.USERS"), itemStyle: { whiteSpace: 'nowrap' }, key: "Users", render: (item) => (
            //         <Text size="sm">{(item.users || []).filter(u => u.roles && u.roles.length > 0 && !(u.roles || []).some(r => ["Client"].includes(r.profile?.title))).length}</Text>
            //     )
            // },
            // {
            //     title: str("COMPANIES.USAGE"), itemStyle: { whiteSpace: 'nowrap' }, key: "Usage", render: (item) => (
            //         <Text size="sm">{formatMilliseconds(item.billing?.consumption * 1000)} - {`${formatMoney(item.billing?.creditsUsage)} / ${formatMoney(item.billing?.totalCredits ?? 0)}`}</Text>
            //     )
            // },
            // {
            //     title: str("COMPANIES.PLAN"), itemStyle: { whiteSpace: 'nowrap' }, key: "Plan", render: (item) => (
            //         <Text size="sm">{item.billing?.plan?.title ?? "Free"}</Text>
            //     )
            // },
            // {
            //     title: str("COMPANIES.STATUS"), itemStyle: { whiteSpace: 'nowrap' }, key: "Status", render: (item) => (
            //         <Text size="sm">{item.billing?.activePaymentMethod
            //             ? moment().diff(moment(item?.billing?.nextPayment), "days") > 0
            //                 ? "Not Payed"
            //                 : "Active"
            //             : "Canceled"}</Text>
            //     )
            // },
            // {
            //     title: str("COMPANIES.NEXT_PAYMENT"), itemStyle: { whiteSpace: 'nowrap' }, key: 'next-payment', render: (item) => <Box>
            //         <Text size="sm">{item.active_plan?.plan_type !== "one-time" && item.billing?.activePaymentMethod && `${getExtenseDatetime(item?.billing?.nextPayment, false)}`}</Text>
            //     </Box>
            // },
            // { title: "Actions", key: 'actions', render: (item) => {
            //     let roles = (item.users || []).reduce((pv, u) => pv.concat(u.roles), []);
            //     let role = roles.find(r => ["Owner", "Admin"].includes(r.profile?.title))
            //     return role ? <Box>
            //         <ActionIcon
            //             onClick={() => {
            //                 navigate("/")
            //                 onChangeRole(role.id)
            //             }}
            //             variant='outline'
            //         >
            //             <FaSignInAlt />
            //         </ActionIcon>
            //     </Box> : null
            // }},
        ]

    const loadCompanies = () => {
        setError(null);
        setLoading(true);

        GET_ALL_COMPANIES()
            .then(companies => {
                setLoading(false);
                setData(companies);
            })
            .catch(err => {
                setLoading(false);
                setError(err.message);
                notifications.show({ message: err.message, color: 'red' });
            })
    }

    const loadBilling = () => {
        setUpdating(u => ({ ...u, loadingBilling: true }))
        GET_BILLING_DETAILS(updating?.item?.id)
            .then(billing => {
                setUpdating(u => ({ ...u, billing }));
                setPlanId(billing?.plan?.id);
            })
            .catch(err => {
                showNotification({ message: err.message, color: "red" });
            })
            .finally(() => {
                setUpdating(u => ({ ...u, loadingBilling: false }))
            })
    }

    useEffect(() => {
        setPlanId(updating?.billing?.plan?.id);
        setNextPayment(moment(updating?.billing?.nextPayment).format());
    }, [updating]);

    useEffect(() => {
        if (updating?.item?.id) {
            loadBilling()
        }
    }, [updating?.item?.id])

    const refresh = () => {
        loadCompanies();
        loadPlans();
    }

    React.useEffect(refresh, []);

    React.useEffect(() => {
        let m = moment("2024-01-01T00:00:00");
        let ms = [];
        while (m < moment().endOf("month")) {
            ms.push({ base: m.format() })
            m.add(1, "months");
        }
        setMonths(ms);
    }, []);

    React.useImperativeHandle(ref, () => ({
        refresh: () => { refresh(); }
    }));

    return <>
        <Paper p="sm" mt="md">
            <Group mb="md">
                {/* <Button loading={loadingExport} variant='outline' onClick={() => setTypeView(p => p === "payments" ? "general" : "payments")}>
                    {typeView === "payments" ? str("COMPANIES.GENERAL_VIEW") : str("COMPANIES.BILLING_VIEW")}
                </Button> */}
                <Button loading={loadingExport} onClick={handleExport}>{str("COMPANIES.EXPORT")}</Button>
            </Group>
            <Stack>
                <Paper p="xs" style={{ backgroundColor: theme.colors.dark[8] }}>
                    <Grid align="center">
                        {COLUMNS.map((c) => <Grid.Col span={{ md: 3 }}>
                            <Text ta="center" size="lg">{c.title}</Text>
                        </Grid.Col>)}
                        <Grid.Col span={{ md: 3 }}>

                        </Grid.Col>
                    </Grid>
                </Paper>
                {
                    data.map(item => (
                        <Paper p="xs" shadow='xs' style={{ backgroundColor: theme.colors.dark[6] }}>
                            <Grid align="center">
                                {COLUMNS.map((c) => <Grid.Col span={{ md: 3 }}>
                                    <Text lineClamp={1} ta="center" size="sm">{c.render(item)}</Text>
                                    {/* <Text ta="center" size="xs">{c.title}</Text> */}
                                </Grid.Col>)}
                                <Grid.Col span={{ md: 3 }}>
                                    <Button onClick={() => setUpdating({ item })} fullWidth bg="dark.6" size="md">{str("EDIT")}</Button>
                                </Grid.Col>
                            </Grid>
                        </Paper>
                    ))
                }
            </Stack>
            <Modal
                title={
                    <Group>
                        {!!updating?.type && <ActionIcon variant="outline" onClick={() => setUpdating(u => ({ ...u, type: undefined }))}><FaArrowLeft /></ActionIcon>}
                        <Text>
                            {{
                                "plan": str("COMPANIES.CHANGE_PLAN"),
                                "delete": str("COMPANIES.DELETE_WORKSPACE"),
                                "credits": str("COMPANIES.ADD_CREDITS"),
                                "package": str("COMPANIES.ADD_PACKAGE"),
                            }[updating?.type as string] ?? str("COMPANIES.UPDATE_WORKSPACE")}
                        </Text>
                    </Group>
                }
                size="sm"
                opened={!!updating}
                onClose={() => setUpdating(null)}
            >
                <Stack>
                    {updating?.loadingBilling && <Loader />}
                    <Group>
                        <Text>{str("COMPANIES.USAGE")}</Text>
                    </Group>
                    <Group justify='space-between'>
                        {
                            updating?.billing?.plan?.plan_type !== "packages"
                                ? <Text size="sm">{formatMilliseconds(updating?.billing?.consumption * 1000)} - {`${formatMoney(updating?.billing?.creditsUsage)} / ${formatMoney(updating?.billing?.totalCredits ?? 0)}`}</Text>
                                : <Text size="sm">{`${formatMilliseconds((updating?.billing?.packagesUsage ?? 0) * 1000)} / ${formatMilliseconds((updating?.billing?.totalPackages ?? 0) * 1000)}`}</Text>
                        }
                        <ActionIcon
                            onClick={() => setAddingCredits({})}
                        >
                            <FaPlus />
                        </ActionIcon>
                    </Group>
                    {!updating?.item?.active_wl_plan && <>
                        <Text>{str("COMPANIES.PLAN")}</Text>
                        <InputField
                            name="plan"
                            value={planId}
                            onChange={({ plan }) => handleChangePlan(plan)}
                            fieldType={InputFieldTypes.SELECT}
                            clearable={false}
                            searchable={false}
                            options={plans.map(plan => ({ value: plan.id, label: `${plan.title} (${extenseRecurrenceType(plan.recurrence_type)})` }))}
                        />
                        {/* <Text>{str("COMPANIES.STATUS")}</Text>
                    <Text size="sm">{updating?.billing?.activePaymentMethod
                        ? moment().diff(moment(updating?.billing?.nextPayment), "days") > 0
                            ? "Not Payed"
                            : "Active"
                        : "Canceled"}</Text> */}

                        <Text>{str("COMPANIES.ACTIVE_UNTIL")}</Text>
                        <InputField
                            name="payment"
                            value={nextPayment ? moment(nextPayment).toDate() : undefined}
                            onChange={({ payment }) => handleChangeNextPayment(moment(payment).format())}
                            fieldType={InputFieldTypes.DATE}
                            clearable={false}
                            searchable={false}
                        />
                        {/* <Text size="sm">{updating?.item?.active_plan?.plan_type !== "one-time" && updating?.billing?.activePaymentMethod && `${getExtenseDatetime(updating?.billing?.nextPayment, false)}`}</Text> */}
                    </>}

                    <Group justify='flex-end'>
                        {!updating?.item?.active_wl_plan && <Button loading={loadingDelete} color="red" variant='light' onClick={() => {
                            confirmDialog(
                                { text: str("COMPANIES.DELETE_CONFIRMATION_TITLE") },
                                (({ confirmed }) => {
                                    if (confirmed) handleDeleteWorkspace(updating?.item?.id);
                                })
                            )
                        }}>{str("COMPANIES.DELETE_WORKSPACE")}</Button>}
                    </Group>
                </Stack>
            </Modal>

            <Modal
                size="sm"
                opened={!!addingCredits}
                onClose={() => setAddingCredits(null)}
            >
                <Stack>
                    {
                        updating?.billing?.plan?.plan_type !== 'packages'
                            ? <>
                                <InputField
                                    title={str(`COMPANIES.CREDITS_TO_ADD`)}
                                    placeholder='$ 10.00'
                                    value={addingCredits?.credits}
                                    name="credits"
                                    mask={InputFieldMasks.MONEY}
                                    onChange={({ credits }) => setAddingCredits(ac => ac ? ({ ...ac, credits }) : ac)}
                                />
                                <Button onClick={handleAddCredits} disabled={!addingCredits?.credits}>{str("SAVE")}</Button>
                            </>
                            : <>
                                <InputField
                                    title={str(`COMPANIES.MINUTES_TO_ADD`)}
                                    placeholder='10 min'
                                    value={addingCredits?.minutes}
                                    name="minutes"
                                    fieldType={InputFieldTypes.NUMBER}
                                    onChange={({ minutes }) => setAddingCredits(ac => ac ? ({ ...ac, minutes }) : ac)}
                                />
                                <Button onClick={handleAddCredits} disabled={!addingCredits?.minutes}>{str("SAVE")}</Button>

                            </>
                    }
                </Stack>
            </Modal>
            {/* <Table
                mb="md"
                data={data}
                columns={COLUMNS}
                error={error}
                loading={loading}
                actions={[
                    // {
                    //     title: "Deletar", 
                    //     color: "red", 
                    //     loading: item => Boolean(loadingDelete[item.id]), 
                    //     onClick: ({item}) => {
                    //         app.confirmDialog(
                    //             {text: "Realmente deseja deletar esse usuário?"}, 
                    //             (({confirmed}) => {
                    //                 if(confirmed) deleteUser(item.id);
                    //             })
                    //         )
                    //     }
                    // }
                ]}
            /> */}
        </Paper >
    </>
}

export default React.forwardRef(List);